import { Env } from '@constants/settings';
import { SERVICE_PRINCIPAL_DOMAIN } from '@pages/api-portal/constants';

type TokenResponse = {
    access_token?: string;
};

/*
 * Obtains a bearer token for a service principal via a Keycloak resource-owner password
 * grant against the app's own tenant realm (Env.keycloakUrl/keycloakRealm) — a separate
 * auth context from the current user's session, used only to execute a real request in
 * the Try It Out console.
 */
export async function getServicePrincipalToken(username: string, password: string): Promise<string> {
    const tokenUrl = `${Env.keycloakUrl.replace(/\/$/, '')}/realms/${Env.keycloakRealm}/protocol/openid-connect/token`;

    const formData = new URLSearchParams();
    formData.append('client_id', Env.keycloakClientId);
    formData.append('grant_type', 'password');
    formData.append('scope', 'openid');
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Authentication failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data: TokenResponse = await response.json();
    if (!data.access_token) {
        throw new Error('No access token received from authentication server');
    }

    return data.access_token;
}

/*
 * Builds the auto-provisioned DataStudio username for a service principal.
 */
export function getServicePrincipalUsername(name: string): string {
    return `${name}@${SERVICE_PRINCIPAL_DOMAIN}`;
}
